import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import * as Scrivito from "scrivito"
import { ContentTag, isInPlaceEditingActive } from "scrivito"
import { BuildingReferenceObj } from "scrivito/Objs/BuildingReference/BuildingReferenceObjClass"
import { ScrivitoLink } from "scrivito/scrivito-link/ScrivitoLink"
import { raiseToast } from "shared/lib/toast/raiseToast"
import { useEvent } from "shared/lib/useEvent"
import { ToastType } from "shared/types/Toast"
import { Tag } from "shared/ui/tags/Tags"

import { ParseKeys } from "i18next"

import { BuildingReferencePageGeneralInfoViewModel } from "../BuildingReferencePageGeneralInfo"
import { useBuildingReferenceGeneralInfoCopiedText } from "./useBuildingReferenceGeneralInfoCopiedText"

interface UseBuildingReferencePageGeneralInfoViewModelProps {
  buildingReferenceObj: BuildingReferenceObj
}

export function useBuildingReferencePageGeneralInfoViewModel({
  buildingReferenceObj
}: UseBuildingReferencePageGeneralInfoViewModelProps): BuildingReferencePageGeneralInfoViewModel {
  const { t } = useTranslation()
  const generalInfoText =
    useBuildingReferenceGeneralInfoCopiedText(buildingReferenceObj)

  const tags = useMemo(
    () =>
      buildingReferenceObj?.get("buildingType")?.map(
        buildingType =>
          ({
            key: buildingType,
            title: t(
              `BuildingReference.BuildingType.${buildingType}` as ParseKeys
            )
          } as Tag)
      ),
    [buildingReferenceObj?.get("buildingType")]
  )

  const handleCopyToClipboard = useEvent(() => {
    navigator.clipboard
      .writeText(generalInfoText)
      .then(() => {
        raiseToast({
          message: t("General.ItemCopied", {
            item: t("BuildingReference.GeneralInformation")
          }),
          type: ToastType.SUCCESS
        })
      })
      .catch(() => {
        raiseToast({
          message: t("General.ItemCopyFailed", {
            item: t("BuildingReference.GeneralInformation")
          }),
          type: ToastType.ERROR
        })
      })
  })

  return {
    tags,
    planner:
      buildingReferenceObj.get("planner") || isInPlaceEditingActive() ? (
        <ContentTag content={buildingReferenceObj} attribute="planner" />
      ) : null,
    owner:
      buildingReferenceObj.get("owner") || isInPlaceEditingActive() ? (
        <ContentTag content={buildingReferenceObj} attribute="owner" />
      ) : null,
    architect:
      buildingReferenceObj.get("architect") || isInPlaceEditingActive() ? (
        <ContentTag content={buildingReferenceObj} attribute="architect" />
      ) : null,
    contact:
      buildingReferenceObj.get("contact") || isInPlaceEditingActive() ? (
        <ContentTag content={buildingReferenceObj} attribute="contact" />
      ) : null,
    locationContactDetails:
      buildingReferenceObj.get("locationContactDetails") ||
      isInPlaceEditingActive() ? (
        <ContentTag
          content={buildingReferenceObj}
          attribute="locationContactDetails"
        />
      ) : null,
    architectContactDetails:
      buildingReferenceObj.get("architectContactDetails") ||
      isInPlaceEditingActive() ? (
        <ContentTag
          content={buildingReferenceObj}
          attribute="architectContactDetails"
        />
      ) : null,
    clientContactDetails:
      buildingReferenceObj.get("clientContactDetails") ||
      isInPlaceEditingActive() ? (
        <ContentTag
          content={buildingReferenceObj}
          attribute="clientContactDetails"
        />
      ) : null,
    plannerContactDetails:
      buildingReferenceObj.get("plannerContactDetails") ||
      isInPlaceEditingActive() ? (
        <ContentTag
          content={buildingReferenceObj}
          attribute="plannerContactDetails"
        />
      ) : null,
    street:
      buildingReferenceObj.get("contact")?.get("street") ||
      isInPlaceEditingActive() ? (
        <ContentTag
          content={buildingReferenceObj.get?.("contact")}
          attribute="street"
        />
      ) : null,
    city:
      buildingReferenceObj.get("contact")?.get("city") ||
      isInPlaceEditingActive() ? (
        <ContentTag
          content={buildingReferenceObj.get?.("contact")}
          attribute="city"
        />
      ) : null,
    country:
      buildingReferenceObj.get("contact")?.get("country") ||
      isInPlaceEditingActive() ? (
        <ContentTag
          content={buildingReferenceObj.get?.("contact")}
          attribute="country"
        />
      ) : null,
    mapsURL:
      buildingReferenceObj.get("contact")?.get("mapsURL") ||
      isInPlaceEditingActive() ? (
        <ScrivitoLink
          className="link"
          to={buildingReferenceObj.get("contact")?.get("mapsURL")}
          target="_blank"
        >
          {(
            buildingReferenceObj.get("contact")?.get("mapsURL") as Scrivito.Link
          )?.title()}
        </ScrivitoLink>
      ) : null,
    additionalInformation:
      buildingReferenceObj.get("description") || isInPlaceEditingActive() ? (
        <ContentTag content={buildingReferenceObj} attribute="description" />
      ) : null,

    copyToClipboard: handleCopyToClipboard
  }
}
